import Image from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard } from "../types";
import { STORY_BADGE_STYLES } from "./category-badge-styles";
import { HighlightedText } from "./highlighted-text";

const MATERIAL_TYPE_LABELS: Partial<
  Record<ExploreStoryCard["materialType"], string>
> = {
  PROMPT: "Prompt",
  WORKFLOW: "Workflow",
  RELEASE: "Release",
  DEAL: "Deal",
};

interface MobileStoryTextCardProps {
  story: ExploreStoryCard;
  exploreBase: string;
  /** Show divider below the card (default true) */
  showDivider?: boolean;
  /** Optional search query — used to highlight matched terms in the title. */
  highlightQuery?: string;
}

/**
 * New Yorker–style text-first mobile card.
 *
 * Layout: category label · headline (up to 4 lines) · summary ·
 * tag + time. Small square thumbnail floats right.
 * Maximises headline real-estate on narrow screens.
 */
export function MobileStoryTextCard({
  story,
  exploreBase,
  showDivider = true,
  highlightQuery,
}: MobileStoryTextCardProps) {
  const primaryTag = story.productTags[0] ?? story.topicTags[0];
  const _categoryLabel = story.categoryDisplayName;
  const materialLabel = MATERIAL_TYPE_LABELS[story.materialType];
  const badgeStyleClass = STORY_BADGE_STYLES[story.storyBadge];
  const shouldOptimizeImage = story.coverImageUrl
    ? canOptimizeExploreImage({ src: story.coverImageUrl })
    : false;

  return (
    <div>
      {/* "New" badge — sits above the headline+thumbnail row */}
      {story.isNew && (
        <div className="mb-1.5 flex items-center gap-1">
          <span className="h-1.5 w-1.5 animate-[explore-pulse-dot_1.5s_ease-in-out_infinite] rounded-full bg-[var(--explore-new-indicator)]" />
          <span className="font-bold text-[0.65rem] text-[var(--explore-new-indicator)] uppercase tracking-[0.04em]">
            New
          </span>
        </div>
      )}

      <Link
        className="flex gap-2.5 no-underline active:opacity-80"
        href={`${exploreBase}/stories/${story.storyId}`}
        style={{ fontFamily: "var(--homepage-sans-stack)" }}
      >
        {/* Text content — takes all available width */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Headline — always reads first */}
          <h3
            className="mb-1 line-clamp-3 text-pretty font-bold text-[1.05rem] text-[var(--explore-text-primary)] leading-[1.25] min-[360px]:text-[1.1rem] min-[360px]:leading-[1.28]"
            style={{ fontFamily: "var(--explore-card-title-stack)" }}
          >
            <HighlightedText query={highlightQuery} text={story.headline} />
          </h3>

          {/* Summary — secondary info scent */}
          <p className="mb-1.5 line-clamp-2 text-[0.85rem] text-[var(--explore-text-secondary)] leading-[1.4] min-[360px]:text-[0.88rem]">
            <HighlightedText query={highlightQuery} text={story.summary} />
          </p>

          {/* Material badge → category emoji → tag → time */}
          <div className="flex flex-wrap items-center gap-0.5">
            {materialLabel && (
              <span
                className={`inline-flex items-center rounded-sm px-1.5 py-0.5 font-bold text-[0.55rem] uppercase tracking-[0.05em] ${badgeStyleClass}`}
              >
                {materialLabel}
              </span>
            )}
            {story.categoryEmoji && (
              <span className="inline-flex items-center justify-center rounded-sm bg-[var(--explore-tag-bg)] px-1 py-0.5 text-[0.62rem]">
                {story.categoryEmoji}
              </span>
            )}
            {primaryTag && (
              <span className="inline-flex items-center rounded-sm bg-[var(--explore-tag-bg)] px-1.5 py-0.5 font-medium text-[0.58rem] text-[var(--explore-tag-text)]">
                {primaryTag.displayName}
              </span>
            )}
            <span className="text-[0.6rem] text-[var(--explore-text-muted)]">
              {story.timeAgo}
            </span>
          </div>
        </div>

        {/* Small thumbnail on right — aligned with headline, not the NEW badge */}
        {story.coverImageUrl && (
          <div className="relative h-[60px] w-[60px] shrink-0 self-start overflow-hidden rounded-lg bg-gradient-to-br from-[var(--explore-tag-bg)] to-[var(--explore-border-light)] min-[360px]:h-[66px] min-[360px]:w-[66px]">
            <Image
              alt=""
              className="h-full w-full object-cover"
              fill
              sizes="(max-width: 359px) 60px, 66px"
              src={story.coverImageUrl}
              unoptimized={!shouldOptimizeImage}
            />
          </div>
        )}
      </Link>

      {/* Editorial divider */}
      {showDivider && (
        <div className="mt-4 border-[var(--explore-border-light)] border-b" />
      )}
    </div>
  );
}
